import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as AttachMoneyIcon,
  Add as AddIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";
import apiService from "../../../service/Api";
import { RootState } from "../../../store/Index";
import StatsCards from "./StatsCard";
import QuickActionCard from "./QuickActionCards";
import CustomeDataGrid from "../../../components/common/CustomeDataGrid";
import { IProposal, ProposalApiResponse } from "./types/proposal.types";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";
import formatDate from "../../../utils/FormatDate";

export const ProposalDashboards: React.FC = () => {
  const navigate = useNavigate();
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const [proposals, setProposals] = useState<IProposal[]>([]);
  const [rows, setRows] = useState<IProposal[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);

  const handleNewProposal = () => {
    navigate("/proposals/proposals-builder");
  };

  const handleProposalRowClick = async (proposalId: number) => {
    try {
      const response = await apiService.get<IProposal>(
        `/api/bookings/booking-proposal/${proposalId}/`
      );
      const proposal = response.data;

      navigate("/proposals/proposals-builder", { state: { proposal } });
    } catch (error) {
      console.error("Error fetching proposal:", error);
      showErrorToast("Failed to load proposal. Please try again.");
    }
  };

  const fetchProposals = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get<ProposalApiResponse>(
        `/api/bookings/booking-proposal/?page=${
          page + 1
        }&page_size=${pageSize}&search=${searchQuery}`
      );

      const proposals = response.data.results.map((p: any) => {
        const deceased = p?.booking?.deceased?.[0];
        const event = p?.booking?.events?.[0];
        const deceasedName = deceased
          ? `${deceased.first_name ?? ""} ${deceased.surname ?? ""}`.trim()
          : "";
        const eventTitle = event?.title ?? "";

        return {
          ...p,
          client_name: `${p.booking?.contact?.first_name ?? ""} ${
            p.booking?.contact?.last_name ?? ""
          }`.trim(),
          deceased_name: deceasedName,
          event_title: eventTitle,
          display_title:
            deceasedName && eventTitle
              ? `${deceasedName} - ${eventTitle}`
              : deceasedName || eventTitle || "Untitled",
          created_at: formatDate(p.created_at),
        };
      });

      setRows(proposals);
      setProposals(proposals);
      setTotalCount(response.data.count ?? 0);
    } catch (error) {
      console.error("Error fetching proposals:", error);
      showErrorToast("Failed to load proposals. Please try again.");
      setError("Failed to load proposals. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProposals();
  }, [page, pageSize, searchQuery]);

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      await apiService.patch(`/api/bookings/booking-proposal/${id}/`, {
        status: newStatus,
      });

      setRows((prev) =>
        prev.map((p) => (p.id === id ? { ...p, status: newStatus } : p))
      );

      showSuccessToast(`Status updated to "${newStatus}"`);
    } catch (error) {
      console.error("Failed to update status:", error);
      showErrorToast("Failed to update status. Please try again.");
    }
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5 },
    { field: "display_title", headerName: "Title", flex: 1 },
    { field: "client_name", headerName: "Client", flex: 1 },
    { field: "created_at", headerName: "Created At", flex: 1 },
    {
      field: "additional_notes",
      headerName: "Additional Notes",
      flex: 1,
      renderCell: (params: any) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            lineHeight: 1.4,
            overflow: "visible",
            marginTop: 4,
          }}
        >
          {params.value || "—"}
        </div>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params: any) => {
        const status = params.value?.toLowerCase();
        const id = params.row.id;

        return (
          <FormControl fullWidth>
            <Select
              value={status || "draft"}
              onChange={(e) => handleStatusChange(id, e.target.value)}
              onClick={(event) => event.stopPropagation()}
              MenuProps={{
                onClick: (event) => event.stopPropagation(),
              }}
              sx={{
                "& .MuiSelect-select": {
                  padding: "4px 8px",
                  width: "100%",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
            >
              <MenuItem value="draft">
                <Chip
                  label="Draft"
                  size="small"
                  variant="outlined"
                  sx={{
                    bgcolor: "#F79E1B40",
                    color: "#F79E1B",
                    width: "100%",
                    "& .MuiChip-label": { fontWeight: 500 },
                  }}
                />
              </MenuItem>

              <MenuItem value="final">
                <Chip
                  label="Final"
                  color="success"
                  size="small"
                  variant="outlined"
                  sx={{
                    bgcolor: "#0000FF26",
                    color: "#0000FF",
                    width: "100%",
                    "& .MuiChip-label": { fontWeight: 500 },
                  }}
                />
              </MenuItem>
            </Select>
          </FormControl>
        );
      },
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4" fontWeight="600">
          Proposals Dashboard
        </Typography>
        <Button
          variant="contained"
          onClick={handleNewProposal}
          sx={{
            bgcolor: reduxTheme.primary_colors[400],
          }}
        >
          New Proposal
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCards
            title="Total Proposals"
            value={totalCount}
            icon={<DescriptionIcon sx={{ fontSize: 40 }} />}
            iconColor="#3f51b5"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCards
            title="Accepted"
            value={0}
            icon={<CheckCircleIcon sx={{ fontSize: 40 }} />}
            iconColor="#4caf50"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCards
            title="Total Value"
            value="$0"
            icon={<AttachMoneyIcon sx={{ fontSize: 40 }} />}
            iconColor="#ff9800"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCards
            title="Accepted Value"
            value="$0"
            icon={<CheckCircleIcon sx={{ fontSize: 40 }} />}
            iconColor="#4caf50"
          />
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
          Quick Actions
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <QuickActionCard
              title="Create New Proposal"
              description="Start a new customized proposal"
              icon={<AddIcon sx={{ fontSize: 32 }} />}
              backgroundColor="#e8eaf6"
              iconColor="#3f51b5"
              onClick={handleNewProposal}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <QuickActionCard
              title="Generate Reports"
              description="View business analytics"
              icon={<AssessmentIcon sx={{ fontSize: 32 }} />}
              backgroundColor="#fff3e0"
              iconColor="#ff9800"
              onClick={() => {}}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Data Section */}
      <Card sx={{ boxShadow: 0 }}>
        <CardContent>
          <Typography variant="h5" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
            Recent Proposals
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : proposals.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
              }}
            >
              <DescriptionIcon sx={{ fontSize: 80, color: "#bdbdbd", mb: 2 }} />
              <Typography variant="h6" fontWeight="600" gutterBottom>
                No proposals yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first proposal to get started
              </Typography>
              <Button
                variant="contained"
                onClick={handleNewProposal}
                sx={{
                  bgcolor: reduxTheme.primary_colors[400],
                  px: 4,
                  py: 1.5,
                }}
              >
                Create First Proposal
              </Button>
            </Box>
          ) : (
            <CustomeDataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              rowCount={totalCount}
              paginationMode="server"
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={(model) => {
                setPage(model.page);
                setPageSize(model.pageSize);
              }}
              navigateToOnRowClick={(id) => {
                handleProposalRowClick(id as number);
                return "";
              }}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
